import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { changePassword, user, company } = useAuth();
  const [form, setForm] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.new !== form.confirm) {
      toast.error('Yeni şifreler eşleşmiyor', { duration: 5000 });
      return;
    }
    
    if (form.new.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı', { duration: 5000 });
      return;
    }
    
    if (form.new === form.current) {
      toast.error('Yeni şifre eski şifreden farklı olmalı', { duration: 5000 });
      return;
    }
    
    setLoading(true);
    try {
      await changePassword(form.current, form.new);
      toast.success('Şifreniz başarıyla değiştirildi');
      navigate('/erp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre değiştirilemedi', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleShow = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="min-h-screen bg-[#e8dfd0] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#faf7f2] border-[#d0c4b0] shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#7a6a2e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={32} className="text-[#7a6a2e]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1a1916]">Şifre Değiştirme Zorunlu</CardTitle>
          <CardDescription className="text-[#8a7e70]">
            Güvenliğiniz için geçici şifrenizi değiştirmeniz gerekmektedir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* User & Company Info */}
          <div className="bg-[#e8dfd0]/50 rounded-lg p-3 mb-6 text-center">
            <p className="text-sm font-medium text-[#1a1916]">{user?.name}</p>
            <p className="text-xs text-[#8a7e70]">{company?.name}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Geçici Şifre (Mevcut)</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={form.current}
                  onChange={(e) => setForm(f => ({ ...f, current: e.target.value }))}
                  className="bg-white border-[#d0c4b0] pr-10"
                  placeholder="Size verilen geçici şifre"
                  required
                  data-testid="force-current-password"
                />
                <button
                  type="button"
                  onClick={() => toggleShow('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={form.new}
                  onChange={(e) => setForm(f => ({ ...f, new: e.target.value }))}
                  className="bg-white border-[#d0c4b0] pr-10"
                  placeholder="En az 6 karakter"
                  required
                  data-testid="force-new-password"
                />
                <button
                  type="button"
                  onClick={() => toggleShow('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Yeni Şifre (Tekrar)</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))}
                  className="bg-white border-[#d0c4b0] pr-10"
                  placeholder="Yeni şifreyi tekrar girin"
                  required
                  data-testid="force-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => toggleShow('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] font-semibold h-11 mt-6"
              data-testid="force-change-password-btn"
            >
              {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir ve Devam Et'}
            </Button>
          </form>

          <p className="text-xs text-[#8a7e70] text-center mt-4">
            Bu işlemi atlamanız mümkün değildir. Lütfen güvenli bir şifre belirleyin.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
